import React, { useState, useEffect, useMemo } from "react";
import {
  Row, Col, Card, Typography, Table, Empty, Select, Modal,
  Button, Space, Avatar, Statistic, DatePicker, message, Spin, Tag, Divider
} from "antd";
import {
  DollarOutlined, ShoppingCartOutlined, WarningOutlined, EyeOutlined,
  BarChartOutlined, PieChartOutlined, FileTextOutlined, ArrowUpOutlined,
  ArrowDownOutlined, CalendarOutlined, RiseOutlined, FallOutlined,
  TeamOutlined, CreditCardOutlined, ShopOutlined, StarOutlined
} from "@ant-design/icons";
import { Column, Line, DualAxes } from "@ant-design/charts";
import { Pie, Doughnut } from "react-chartjs-2";
import axiosInstance from "./../../axiosInstance";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import moment from 'moment';
import { motion } from "framer-motion";
import { useTheme } from "@emotion/react";

const { Title, Text } = Typography;
const { Option } = Select;

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Enhanced color generation function
const generateComparisonColors = (data, comparisonType) => {
  // Base color palette
  const basePalette = [
    '#422AFB', '#715FFE', '#A192FD', '#D1C9FF',
    '#4FD1C5', '#38B2AC', '#319795', '#2C7A7B',
    '#F6AD55', '#ED8936', '#DD6B20', '#C05621',
    '#4299E1', '#3182CE', '#2B6CB0', '#2C5282',
    '#9F7AEA', '#805AD5', '#6B46C1', '#553C9A'
  ];

  // Generate distinct colors based on comparison type
  if (comparisonType === 'time_period') {
    // Time periods get a gradient from purple to teal
    return data.map((_, i) => {
      const ratio = i / (data.length - 1);
      return interpolateColor('#422AFB', '#4FD1C5', ratio);
    });
  } else if (comparisonType === 'products') {
    // Products get distinct colors from the base palette
    return data.map((_, i) => basePalette[i % basePalette.length]);
  } else if (comparisonType === 'customers') {
    // Customers get warm colors
    return data.map((_, i) => interpolateColor('#F6AD55', '#E53E3E', i / data.length));
  } else {
    // Default to base palette
    return data.map((_, i) => basePalette[i % basePalette.length]);
  }
};

// Helper function to interpolate between two colors
const interpolateColor = (color1, color2, factor) => {
  if (factor <= 0) return color1;
  if (factor >= 1) return color2;

  const hex = (color) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  const rgb1 = hex(color1);
  const rgb2 = hex(color2);

  const r = Math.round(rgb1[0] + factor * (rgb2[0] - rgb1[0]));
  const g = Math.round(rgb1[1] + factor * (rgb2[1] - rgb1[1]));
  const b = Math.round(rgb1[2] + factor * (rgb2[2] - rgb1[2]));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const HomePage = () => {
  const [typingText] = useState("Welcome to Sell Smart Management System");
  const [salesAmount, setSalesAmount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [salesProgress, setSalesProgress] = useState(0);
  const [monthlyProgress, setMonthlyProgress] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);
  const [productSalesData, setProductSalesData] = useState([]);
  const [todaySales, setTodaySales] = useState(0);
  const [todaySalesProducts, setTodaySalesProducts] = useState([]);
  const [salesPeriod, setSalesPeriod] = useState("today");
  const [periodSalesData, setPeriodSalesData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment());
  const [hourlyData, setHourlyData] = useState([]);
  const [todaysInvoices, setTodaysInvoices] = useState([]);
  const [todaysInvoicesCount, setTodaysInvoicesCount] = useState(0);
  const [periodSalesComparison, setPeriodSalesComparison] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonType, setComparisonType] = useState("time_period");
  const [comparisonMetric, setComparisonMetric] = useState("revenue");

  const shopId = localStorage.getItem("shopId");

  const allMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Format current date and time
  const getFormattedDate = () => {
    const today = new Date();
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const day = days[today.getDay()];
    const date = today.getDate();
    const month = allMonths[today.getMonth()];
    const year = today.getFullYear();
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    const seconds = String(today.getSeconds()).padStart(2, "0");

    return `${day}, ${date} ${month} ${year} ${hours}:${minutes}:${seconds}`;
  };

  const [currentDateTime, setCurrentDateTime] = useState(getFormattedDate());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(getFormattedDate());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch comparison data
  const fetchComparisonData = async () => {
    setComparisonLoading(true);
    try {
      const payload = {
        shop_id: shopId,
        comparison_type: comparisonType,
        metric: comparisonMetric
      };

      // Add type-specific parameters
      if (comparisonType === 'time_period') {
        payload.time_period = 'monthly';
        payload.granularity = 'daily';
      } else if (comparisonType === 'products') {
        payload.product_ids = topSellingProducts.slice(0, 5).map(p => p.id);
      } else if (comparisonType === 'customers') {
        // You would need to fetch some customer IDs first
      }

      const response = await axiosInstance.post('/sales/compare', payload);
      setComparisonData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      message.error("Failed to load comparison data");
    } finally {
      setComparisonLoading(false);
    }
  };

  // Fetch today's invoices
  const fetchTodaysInvoices = async () => {
    try {
      const response = await axiosInstance.get(`/sales/todays-invoices/${shopId}`);
      setTodaysInvoices(response.data.invoices || []);
      setTodaysInvoicesCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching today's invoices:", error);
    }
  };

  // Fetch hourly sales data
  const fetchHourlySales = async (selectedDate) => {
    try {
      setLoading(true);
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const response = await axiosInstance.get('/shops/sales/hourly-trend', {
        params: {
          date: formattedDate,
          shopId: shopId
        },
      });

      const newData = response.data.data || [];
      setHourlyData(newData);
    } catch (error) {
      message.error('Failed to load hourly sales data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle date change for hourly sales
  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    fetchHourlySales(selectedDate);
  };

  // Main data fetch effect
  useEffect(() => {
    const fetchData = async () => {
      if (!shopId) {
        console.error("Shop ID not found in local storage");
        return;
      }

      setLoading(true);
      try {
        await fetchTodaysInvoices();
        await fetchHourlySales(date);
        await fetchComparisonData();

        // Fetch product quantities
        const productQuantitiesResponse = await axiosInstance.get(`/products/quantities/${shopId}`);
        setProductCount(productQuantitiesResponse.data.total_product_count || 0);

        // Fetch low stock products
        const lowStockResponse = await axiosInstance.get(`/products/low-stock/${shopId}`);
        const lowStockData = lowStockResponse.data.low_stock_products || [];
        setLowStockCount(lowStockData.length);
        setLowStockProducts(lowStockData);

        // Fetch period sales
        const periodSalesResponse = await axiosInstance.get(`/sales/summary/${shopId}/${salesPeriod}`);
        const periodSalesData = periodSalesResponse.data.sales || [];
        setPeriodSalesData(periodSalesData);

        // Calculate total sales amount
        const totalSales = periodSalesData.reduce((sum, sale) => sum + parseFloat(sale.total), 0);
        setSalesAmount(totalSales);

        // Fetch sales performance
        const salesResponse = await axiosInstance.get(`/sales/performance/${shopId}`);
        const salesData = salesResponse.data.monthly_sales;
        const formattedData = allMonths.map((month, index) => {
          const monthData = salesData.find((item) => item.month === month);
          return { month, sales: monthData ? parseFloat(monthData.total_sales) : 0 };
        });
        setSalesPerformance(formattedData);

        // Calculate progress data
        const progressData = formattedData.map((data, index) => {
          if (index === 0) return { ...data, progress: 0 };
          const previousMonthSales = formattedData[index - 1].sales;
          const progress = previousMonthSales > 0
            ? ((data.sales - previousMonthSales) / previousMonthSales) * 100
            : 0;
          return { ...data, progress: parseFloat(progress.toFixed(2)) };
        });

        setMonthlyProgress(progressData);

        // Calculate overall progress
        const positiveSales = progressData.filter((data) => data.progress > 0);
        const overallProgress = Math.min(
          positiveSales.reduce((sum, data) => sum + data.progress, 0) / (positiveSales.length || 1),
          100
        );
        setSalesProgress(overallProgress);

        // Fetch top selling products
        const productsResponse = await axiosInstance.get(`/sales/top-product/${shopId}`);
        const productsData = productsResponse.data.products || [];
        setTopSellingProducts(productsData);

        // Prepare pie chart data
        const pieData = productsData.map((product) => ({
          type: product.name,
          value: parseFloat(product.quantity_sold),
        }));
        setProductSalesData(pieData);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shopId, salesPeriod, comparisonType, comparisonMetric]);

  // Modal handlers
  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  // Generate colors for charts
  const generateColors = (num) => {
    const colors = [
      '#422AFB', '#715FFE', '#A192FD', '#D1C9FF',
      '#4FD1C5', '#38B2AC', '#319795', '#2C7A7B',
      '#F6AD55', '#ED8936', '#DD6B20', '#C05621'
    ];
    return colors.slice(0, num);
  };

  const generateRandomColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const hue = Math.floor(Math.random() * 360);
      colors.push(`hsl(${hue}, 70%, 60%)`);
    }
    return colors;
  };

  const hourlyChartConfig = useMemo(() => {
    const sortedData = Array.isArray(hourlyData)
      ? [...hourlyData].sort((a, b) => a.label.localeCompare(b.label))
      : [];

    return {
      data: sortedData,
      xField: 'label',
      yField: 'total_sales',
      smooth: true,
      lineStyle: {
        stroke: '#422AFB',
        lineWidth: 3.5,
        shadowColor: 'rgba(66, 42, 251, 0.3)',
        shadowBlur: 10,
        shadowOffsetY: 6
      },
      color: '#422AFB',
      point: {
        size: 5,
        shape: 'circle',
        style: {
          fill: '#422AFB',
          stroke: '#fff',
          lineWidth: 2
        }
      },
      areaStyle: {
        fill: 'l(270) 0:rgba(66, 42, 251, 0.4) 1:rgba(66, 42, 251, 0.02)',
      },
      legend: false,
      xAxis: {
        title: {
          text: 'Hour',
          style: {
            fontWeight: 600,
            fontSize: 13,
            fill: '#8F9BBA'
          },
        },
        label: {
          style: {
            fill: '#8F9BBA',
            fontSize: 11
          }
        },
        line: {
          style: {
            stroke: '#E1E9F8',
          }
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Total Sales (TZS)',
          style: {
            fontWeight: 600,
            fontSize: 13,
            fill: '#8F9BBA'
          },
        },
        label: {
          style: {
            fill: '#8F9BBA',
            fontSize: 11
          },
          formatter: (val) => `Tsh ${Number(val || 0).toLocaleString()}`,
        },
        grid: {
          line: {
            style: {
              stroke: '#E1E9F8',
              lineDash: [4, 5],
            },
          },
        },
      },
      tooltip: {
        showTitle: true,
        title: 'label',
        showMarkers: true,
        fields: ['total_sales', 'total_transactions'],
        formatter: (datum) => {
          return {
            name: 'Total Sales',
            value: `${Number(datum.total_sales).toLocaleString()} TZS`,
          };
        },
        customContent: (title, items) => {
          if (!items || items.length === 0) return null;
          const data = items[0].data;
          return `
          <div style="padding: 12px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); border: 1px solid #f0f0f0;">
            <div style="margin-bottom: 6px; font-weight: 700; color: #1b2559;">${title}</div>
            <div style="color: #422afb; font-weight: 600; margin-bottom: 4px;">Sales: ${Number(data.total_sales).toLocaleString()} TZS</div>
            <div style="color: #8f9bba; font-size: 12px;">Transactions: ${data.total_transactions || 0}</div>
          </div>`;
        }
      },
      interactions: [{ type: 'active-region' }, { type: 'element-active' }],
      padding: [20, 30, 40, 50],
      responsive: true,
      autoFit: true,
    };
  }, [hourlyData]);


  // Sales performance line chart config
  const salesPerformanceConfig = {
    data: salesPerformance,
    xField: 'month',
    yField: 'sales',
    seriesField: 'type',
    lineStyle: {
      stroke: '#422AFB',
      lineWidth: 3.5,
      shadowColor: 'rgba(66, 42, 251, 0.3)',
      shadowBlur: 10,
      shadowOffsetY: 6
    },
    color: '#422AFB',
    point: {
      size: 5,
      shape: 'circle',
      style: {
        fill: '#422AFB',
        stroke: '#fff',
        lineWidth: 2
      }
    },
    smooth: true,
    meta: {
      month: { alias: 'Month' },
      sales: { alias: 'Sales (TZS)' }
    },
    yAxis: {
      min: 0,
      grid: {
        line: {
          style: {
            stroke: '#E1E9F8',
            lineDash: [4, 5],
          },
        },
      },
      label: {
        style: {
          fill: '#8F9BBA',
          fontSize: 11
        },
        formatter: (val) => `Tsh ${Number(val || 0).toLocaleString()}`,
      },
    },
    xAxis: {
      grid: {
        line: {
          style: {
            stroke: '#E1E9F8',
            lineDash: [4, 5],
          },
        },
      },
      label: {
        style: {
          fill: '#8F9BBA',
          fontSize: 11
        }
      },
    },
    interactions: [{ type: 'element-active' }],
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
    areaStyle: {
      fill: 'l(270) 0:rgba(66, 42, 251, 0.4) 1:rgba(66, 42, 251, 0.02)',
    },
  };

  // Product sales pie chart data
  const pieChartData = {
    labels: productSalesData.map((data) => data.type),
    datasets: [{
      data: productSalesData.map((data) => data.value),
      backgroundColor: generateColors(productSalesData.length),
      hoverBackgroundColor: generateColors(productSalesData.length).map(color =>
        color.replace(')', ', 0.8)')
      ),
      borderWidth: 0,
    }],
  };

  // Updated comparison chart config
  const comparisonChartConfig = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0) return null;

    // Generate colors based on comparison type
    const colors = generateComparisonColors(comparisonData, comparisonType);

    const commonConfig = {
      color: colors,
      meta: {
        ['total_' + comparisonMetric]: {
          alias: comparisonMetric === 'revenue' ? 'Revenue (TZS)' :
            comparisonMetric === 'quantity' ? 'Quantity Sold' : 'Profit (TZS)'
        }
      },
      yAxis: {
        label: {
          formatter: (val) => comparisonMetric === 'quantity' ? val : `Tsh ${Number(val || 0).toLocaleString()}`,
        },
      },
      label: {
        position: 'top',
        style: {
          fill: '#1B2559',
          fontWeight: 600,
          fontSize: 12,
          shadowBlur: 2,
          shadowColor: 'rgba(0, 0, 0, 0.45)',
        },
        formatter: (datum) => {
          return comparisonMetric === 'quantity' ? datum['total_' + comparisonMetric] :
            `Tsh ${Number(datum['total_' + comparisonMetric]).toLocaleString()}`;
        },
      },
      interactions: [{ type: 'element-active' }],
      columnStyle: {
        radius: [4, 4, 0, 0],
      },
      legend: {
        position: 'bottom',
        itemName: {
          style: {
            fill: '#8F9BBA',
            fontSize: 12,
          },
          formatter: (name) => name.length > 15 ? `${name.substring(0, 12)}...` : name,
        },
      },
      animation: {
        appear: {
          animation: 'wave-in',
          duration: 1000,
        },
        enter: {
          animation: 'wave-in',
          duration: 1000,
        },
      },
    };

    if (comparisonType === 'time_period') {
      return {
        ...commonConfig,
        data: comparisonData,
        xField: 'period',
        yField: 'total_' + comparisonMetric,
        seriesField: 'period',
        meta: {
          ...commonConfig.meta,
          period: { alias: 'Time Period' },
        },
      };
    } else if (comparisonType === 'products') {
      return {
        ...commonConfig,
        data: comparisonData,
        xField: 'product_name',
        yField: 'total_' + comparisonMetric,
        seriesField: 'product_name',
        meta: {
          ...commonConfig.meta,
          product_name: { alias: 'Product' },
        },
      };
    }

    return null;
  }, [comparisonData, comparisonType, comparisonMetric]);

  // Table columns
  const salesTableColumns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      render: (text) => <Tag color="#4FD1C5">{text}</Tag>
    },
    {
      title: 'Unit Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (text) => (
        <Text strong style={{ color: '#422AFB' }}>
          {`Tsh ${(Number(text) || 0).toLocaleString()}`}
        </Text>
      )
    },
  ];

  const topProductsColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Sold',
      dataIndex: 'quantity_sold',
      key: 'quantity_sold',
      align: 'center',
      render: (text) => <Tag color="#4FD1C5">{text}</Tag>
    },
    {
      title: 'Price',
      dataIndex: 'selling_price',
      key: 'selling_price',
      align: 'right',
      render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
    },
  ];

  // Prepare table data
  const salesTableData = periodSalesData.flatMap(sale =>
    sale.sale_items.map(item => ({
      key: item.id,
      product_name: item.product_name,
      quantity: item.quantity,
      price: item.price,
      total: item.total
    }))
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Welcome Banner Card */}
      <div className="welcome-banner-card" style={{ marginBottom: '24px' }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Title level={2} className="welcome-title" style={{ margin: 0, fontWeight: '800', letterSpacing: '-0.5px' }}>
                {typingText}
              </Title>
              <Text className="welcome-subtitle" style={{ fontSize: '15px', display: 'block', marginTop: '6px' }}>
                Here is your store summary for today. Keep track of sales, stock levels, and performance trends.
              </Text>
            </Col>
            <Col xs={24} md={8} className="welcome-time-col">
              <div className="welcome-time-container">
                <CalendarOutlined style={{ marginRight: '8px' }} />
                <span className="welcome-time-text">{currentDateTime}</span>
              </div>
            </Col>
          </Row>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Total Sales Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
            <Card
              bordered={false}
              loading={loading}
              className="stat-card border-top-sales"
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="stat-card-title">Total Sales</span>
                  <Title level={3} className="stat-card-value">
                    Tsh {salesAmount.toLocaleString()}
                  </Title>
                  <div className="stat-card-trend">
                    {salesProgress >= 0 ? (
                      <ArrowUpOutlined style={{ color: '#01B574', marginRight: '4px' }} />
                    ) : (
                      <ArrowDownOutlined style={{ color: '#E31A1A', marginRight: '4px' }} />
                    )}
                    <span>Active Store</span>
                  </div>
                </div>
                <div className="stat-card-icon-container bg-sales">
                  <DollarOutlined className="stat-card-icon color-sales" />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Products Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
            <Card
              bordered={false}
              loading={loading}
              className="stat-card border-top-products"
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="stat-card-title">Total Products</span>
                  <Title level={3} className="stat-card-value">
                    {productCount}
                  </Title>
                  <div className="stat-card-trend">
                    <RiseOutlined style={{ color: '#01B574', marginRight: '4px' }} />
                    <span>In Catalog</span>
                  </div>
                </div>
                <div className="stat-card-icon-container bg-products">
                  <ShoppingCartOutlined className="stat-card-icon color-products" />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Today's Invoices Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
            <Card
              bordered={false}
              loading={loading}
              className="stat-card border-top-invoices"
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="stat-card-title">Today's Invoices</span>
                  <Title level={3} className="stat-card-value">
                    {todaysInvoicesCount}
                  </Title>
                  <div className="stat-card-trend">
                    <FileTextOutlined style={{ color: '#3182CE', marginRight: '4px' }} />
                    <span>Processed today</span>
                  </div>
                </div>
                <div className="stat-card-icon-container bg-invoices">
                  <FileTextOutlined className="stat-card-icon color-invoices" />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>

        {/* Low Stock Card */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
            <Card
              bordered={false}
              loading={loading}
              className="stat-card border-top-low-stock"
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="stat-card-title">Low Stock Items</span>
                  <Title level={3} className={`stat-card-value ${lowStockCount > 0 ? 'color-low-stock-alert' : ''}`}>
                    {lowStockCount}
                  </Title>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={showModal}
                    className="stat-card-button"
                  >
                    View Details
                  </Button>
                </div>
                <div className={`stat-card-icon-container ${lowStockCount > 0 ? 'bg-low-stock-alert' : 'bg-low-stock'}`}>
                  <WarningOutlined className={`stat-card-icon ${lowStockCount > 0 ? 'color-low-stock-alert' : 'color-low-stock'}`} />
                </div>
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Hourly Sales Trend */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card
              bordered={false}
              loading={loading}
              className="dashboard-card"
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="dashboard-card-title">
                    <CalendarOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                    Hourly Sales Trend
                  </span>
                  <DatePicker
                    value={date}
                    onChange={handleDateChange}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    allowClear={false}
                    style={{ width: '150px' }}
                    suffixIcon={<CalendarOutlined />}
                  />
                </div>
              }
            >
              <div style={{ minHeight: '300px' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', paddingTop: 100 }}>
                    <Spin size="large" />
                  </div>
                ) : hourlyData && hourlyData.length > 0 ? (
                  <Line {...hourlyChartConfig} height={300} />
                ) : (
                  <Empty description="No sales data available for this date" />
                )}
              </div>
            </Card>
          </motion.div>
        </Col>

        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card
              bordered={false}
              loading={loading}
              className="dashboard-card"
              title={
                <span className="dashboard-card-title">
                  <FileTextOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Recent Sales
                </span>
              }
              bodyStyle={{ padding: 0 }}
              extra={
                <Select
                  value={salesPeriod}
                  onChange={setSalesPeriod}
                  style={{ width: 120 }}
                  bordered={false}
                  suffixIcon={<CalendarOutlined />}
                >
                  <Option value="today">Today</Option>
                  <Option value="week">Week</Option>
                  <Option value="month">Month</Option>
                  <Option value="year">Year</Option>
                </Select>
              }
            >
              <Table
                columns={salesTableColumns}
                dataSource={salesTableData}
                rowKey="key"
                size="middle"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  style: { padding: '0 16px 16px' }
                }}
                scroll={{ x: true }}
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  borderTop: 'none'
                }}
                rowClassName={() => 'hover-row'}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>



      {/* Sales Comparison Section */}
      {/* <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card
              bordered={false}
              loading={comparisonLoading}
              style={{
                borderRadius: '16px',
                boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
                backgroundColor: 'white'
              }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: '#1B2559', fontSize: '16px' }}>
                    <BarChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                    Sales Comparison
                  </Text>
                  <Space>
                    <Select
                      value={comparisonType}
                      onChange={setComparisonType}
                      style={{ width: 180 }}
                      bordered={false}
                    >
                      <Option value="time_period">Time Periods</Option>
                      <Option value="products">Products</Option>
                      <Option value="customers">Customers</Option>
                      <Option value="employees">Employees</Option>
                      <Option value="payment_methods">Payment Methods</Option>
                    </Select>
                    <Select
                      value={comparisonMetric}
                      onChange={setComparisonMetric}
                      style={{ width: 120 }}
                      bordered={false}
                    >
                      <Option value="revenue">Revenue</Option>
                      <Option value="quantity">Quantity</Option>
                      <Option value="profit">Profit</Option>
                    </Select>
                  </Space>
                </div>
              }
            >
              <div style={{ height: Math.max(400, comparisonData.length * 40) }}>
                {comparisonData.length > 0 && comparisonChartConfig ? (
                  <Column {...comparisonChartConfig} />
                ) : (
                  <Empty description="No comparison data available" style={{ marginTop: 80 }} />
                )}
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row> */}


      {/* Charts Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* Sales Performance Chart */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card
              bordered={false}
              loading={loading}
              className="dashboard-card"
              title={
                <span className="dashboard-card-title">
                  <BarChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Monthly Sales Performance
                </span>
              }
            >
              <Line {...salesPerformanceConfig} height={300} />
            </Card>
          </motion.div>
        </Col>

        {/* Top Products Pie Chart */}
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card
              bordered={false}
              loading={loading}
              className="dashboard-card"
              title={
                <span className="dashboard-card-title">
                  <PieChartOutlined style={{ marginRight: 8, color: '#422AFB' }} />
                  Product Sales Distribution
                </span>
              }
            >
              {productSalesData.length > 0 ? (
                <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                  <Doughnut
                    data={pieChartData}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'right',
                          labels: {
                            color: localStorage.getItem('darkTheme') === 'true' ? '#a3b1cc' : '#475569',
                            font: {
                              size: 12,
                              weight: '500'
                            },
                            padding: 20,
                            usePointStyle: true,
                            pointStyle: 'circle'
                          }
                        },
                        tooltip: {
                          bodyFont: {
                            size: 14
                          },
                          displayColors: true,
                          usePointStyle: true,
                          callbacks: {
                            label: function (context) {
                              const label = context.label || '';
                              const value = context.raw || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = Math.round((value / total) * 100);
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      },
                      cutout: '65%',
                      borderRadius: 4,
                    }}
                  />
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '40px 0' }}
                />
              )}
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Recent Sales Table */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>

        <Col xs={24} lg={24}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card
              bordered={false}
              loading={loading}
              className="dashboard-card"
              title={
                <span className="dashboard-card-title">
                  <StarOutlined style={{ marginRight: 8, color: '#F6AD55' }} />
                  Top Selling Products
                </span>
              }
              bodyStyle={{ padding: 0 }}
            >
              <Table
                columns={topProductsColumns}
                dataSource={topSellingProducts}
                rowKey="id"
                size="middle"
                pagination={{
                  pageSize: 25,
                  showSizeChanger: false,
                  style: { padding: '0 16px 16px' }
                }}
                scroll={{ x: true }}
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  borderTop: 'none'
                }}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>


      {/* Low Stock Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#E31A1A' }} />
            <Text strong>Low Stock Products</Text>
          </Space>
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        style={{ borderRadius: '16px' }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={[
            {
              title: 'Product',
              dataIndex: 'name',
              key: 'name',
              render: (text) => <Text strong>{text}</Text>
            },
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              key: 'quantity',
              render: (text) => <Tag color={text < 5 ? '#E31A1A' : '#F6AD55'}>{text}</Tag>
            },
            {
              title: 'Price',
              dataIndex: 'selling_price',
              key: 'selling_price',
              align: 'right',
              render: (text) => <Text strong>{`Tsh ${(Number(text) || 0).toLocaleString()}`}</Text>
            },
          ]}
          dataSource={lowStockProducts}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          scroll={{ x: true }}
        />
      </Modal>

      {/* Custom CSS for dashboard redesign */}
      <style jsx global>{`
        /* Welcome Banner Card */
        .welcome-banner-card {
          padding: 28px;
          border-radius: 20px;
          background: linear-gradient(135deg, #ffffff 0%, #f6f9fc 100%);
          border: 1px solid #e2e8f0;
          box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.02);
          position: relative;
          overflow: hidden;
        }
        .dark-theme .welcome-banner-card {
          background: linear-gradient(135deg, #1f1f2e 0%, #151522 100%);
          border: 1px solid #2d2d3f;
          box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.2);
        }
        .welcome-banner-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(66, 42, 251, 0.08) 0%, rgba(66, 42, 251, 0) 70%);
          pointer-events: none;
        }
        .dark-theme .welcome-banner-card::before {
          background: radial-gradient(circle, rgba(113, 95, 254, 0.15) 0%, rgba(113, 95, 254, 0) 70%);
        }
        .welcome-title {
          background: linear-gradient(90deg, #1b2559 0%, #422afb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .dark-theme .welcome-title {
          background: linear-gradient(90deg, #ffffff 0%, #a192fd 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .welcome-subtitle {
          color: #8f9bba;
        }
        .dark-theme .welcome-subtitle {
          color: #a3b1cc;
        }
        .welcome-time-container {
          display: inline-flex;
          align-items: center;
          padding: 8px 16px;
          border-radius: 30px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #422afb;
          font-weight: 600;
          box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.02);
        }
        .dark-theme .welcome-time-container {
          background: #151522;
          border: 1px solid #2d2d3f;
          color: #a192fd;
          box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
        }
        @media (max-width: 768px) {
          .welcome-time-col {
            text-align: left !important;
            margin-top: 12px;
          }
        }

        /* Stat Cards styling */
        .stat-card {
          border-radius: 20px !important;
          box-shadow: 0px 10px 25px rgba(0, 0, 0, 0.015) !important;
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
          overflow: hidden;
        }
        .dark-theme .stat-card {
          background: #1f1f2e !important;
          border: 1px solid #2d2d3f !important;
          box-shadow: 0px 10px 25px rgba(0, 0, 0, 0.15) !important;
        }
        .border-top-sales {
          border-top: 4px solid #01B574 !important;
        }
        .border-top-products {
          border-top: 4px solid #422AFB !important;
        }
        .border-top-invoices {
          border-top: 4px solid #3182CE !important;
        }
        .border-top-low-stock {
          border-top: 4px solid #E53E3E !important;
        }
        .stat-card-title {
          font-size: 14px;
          color: #8f9bba;
          font-weight: 600;
          display: block;
        }
        .dark-theme .stat-card-title {
          color: #a3b1cc;
        }
        .stat-card-value {
          font-size: 26px !important;
          font-weight: 800 !important;
          color: #1b2559 !important;
          margin: 6px 0 !important;
          letter-spacing: -0.5px;
        }
        .dark-theme .stat-card-value {
          color: #ffffff !important;
        }
        .stat-card-trend {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #8f9bba;
          font-weight: 500;
        }
        .dark-theme .stat-card-trend {
          color: #a3b1cc;
        }
        .stat-card-icon-container {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
        }
        .stat-card:hover .stat-card-icon-container {
          transform: scale(1.1);
        }
        .stat-card-icon {
          font-size: 22px;
        }
        .bg-sales {
          background: rgba(1, 181, 116, 0.08);
        }
        .dark-theme .bg-sales {
          background: rgba(1, 181, 116, 0.15);
        }
        .color-sales {
          color: #01B574;
        }
        .bg-products {
          background: rgba(66, 42, 251, 0.08);
        }
        .dark-theme .bg-products {
          background: rgba(161, 146, 253, 0.15);
        }
        .color-products {
          color: #422AFB;
        }
        .dark-theme .color-products {
          color: #a192fd;
        }
        .bg-invoices {
          background: rgba(49, 130, 206, 0.08);
        }
        .dark-theme .bg-invoices {
          background: rgba(66, 153, 225, 0.15);
        }
        .color-invoices {
          color: #3182CE;
        }
        .dark-theme .color-invoices {
          color: #63b3ed;
        }
        .bg-low-stock {
          background: rgba(246, 173, 85, 0.08);
        }
        .bg-low-stock-alert {
          background: rgba(229, 62, 62, 0.08);
        }
        .dark-theme .bg-low-stock-alert {
          background: rgba(229, 62, 62, 0.18);
        }
        .color-low-stock {
          color: #F6AD55;
        }
        .color-low-stock-alert {
          color: #E53E3E !important;
        }
        .stat-card-button {
          padding: 0 !important;
          color: #422AFB !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          height: auto !important;
          margin-top: 4px;
        }
        .dark-theme .stat-card-button {
          color: #a192fd !important;
        }

        /* Container Cards */
        .dashboard-card {
          border-radius: 20px !important;
          box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.015) !important;
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          transition: all 0.3s ease !important;
          margin-bottom: 24px;
        }
        .dark-theme .dashboard-card {
          background: #1f1f2e !important;
          border: 1px solid #2d2d3f !important;
          box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15) !important;
        }
        .dashboard-card-title {
          font-size: 16px !important;
          font-weight: 700 !important;
          color: #1b2559 !important;
          display: flex;
          align-items: center;
        }
        .dark-theme .dashboard-card-title {
          color: #ffffff !important;
        }

        /* Picker inputs */
        .ant-picker {
          border-radius: 8px !important;
          border-color: #e2e8f0 !important;
        }
        .dark-theme .ant-picker {
          background: #151522 !important;
          border-color: #2d2d3f !important;
          color: #ffffff !important;
        }
        .dark-theme .ant-picker-input > input {
          color: #ffffff !important;
        }
        .ant-select-selector {
          border-radius: 8px !important;
        }
        .dark-theme .ant-select-selector {
          background: #151522 !important;
          border-color: #2d2d3f !important;
          color: #ffffff !important;
        }

        /* Table custom designs */
        .hover-row:hover td {
          background-color: #f8fafc !important;
        }
        .dark-theme .hover-row:hover td {
          background-color: #1a1a2e !important;
        }
        .ant-table {
          background: transparent !important;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          border-bottom: 1px solid #cbd5e1 !important;
          padding: 14px 16px !important;
        }
        .dark-theme .ant-table-thead > tr > th {
          background: #151522 !important;
          color: #94a3b8 !important;
          border-bottom: 1px solid #2d2d3f !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 14px 16px !important;
          font-size: 14px !important;
          transition: background-color 0.2s ease;
        }
        .dark-theme .ant-table-tbody > tr > td {
          border-bottom: 1px solid #2d2d3f !important;
        }
        .ant-table-container {
          border-radius: 14px !important;
          overflow: hidden !important;
          border: none !important;
        }
        .ant-pagination-item {
          border-radius: 8px !important;
        }
        .dark-theme .ant-pagination-item {
          background: #151522 !important;
          border-color: #2d2d3f !important;
        }
        .dark-theme .ant-pagination-item-active {
          border-color: #422afb !important;
          background: #422afb !important;
        }
        .dark-theme .ant-pagination-item-active a {
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
};

export default HomePage;